/** One changed line between two versions of a note. */
export interface DiffLine {
  op: "add" | "del";
  text: string;
}

/**
 * The lines removed from `before` and added in `after`, in reading order.
 * Unchanged lines are left out: the history shows what an edit did, and the
 * full text is one click away.
 *
 * A longest-common-subsequence diff by line. Quadratic, which is fine for
 * notes capped at 20,000 characters -- a few hundred lines at most.
 * `before` is empty for a note's first version, so everything is an add.
 */
export function lineDiff(before: string, after: string): DiffLine[] {
  const a = before ? before.split("\n") : [];
  const b = after ? after.split("\n") : [];
  const m = a.length;
  const n = b.length;

  // lcs[i][j]: length of the LCS of a[i..] and b[j..].
  const lcs: number[][] = Array.from({ length: m + 1 }, () => new Array<number>(n + 1).fill(0));
  for (let i = m - 1; i >= 0; i--) {
    for (let j = n - 1; j >= 0; j--) {
      lcs[i][j] = a[i] === b[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }

  const out: DiffLine[] = [];
  let i = 0;
  let j = 0;
  while (i < m && j < n) {
    if (a[i] === b[j]) {
      i++;
      j++;
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      out.push({ op: "del", text: a[i++] });
    } else {
      out.push({ op: "add", text: b[j++] });
    }
  }
  while (i < m) out.push({ op: "del", text: a[i++] });
  while (j < n) out.push({ op: "add", text: b[j++] });
  return out;
}
